// health-check.js - director diagnostics screen (deep-dive lesson 2026-07-02:
// the view timeout and the self-testing RLS hole were both invisible until a
// user hit them). One tap runs every check the CLIENT can honestly perform:
//
//   1. Anonymous lockout - probes sensitive tables the way a stranger would;
//      a table answering OK is a security FAILURE.
//   2. Reporting-view speed - the 57014 lesson (statement timeout at 8s).
//   3. Data-quality tripwires - each one a bug class we actually hit:
//      blank-identity evals, imports published to players.
//   4. Operational freshness - snapshot age, anchor coverage, leftover
//      walkthrough fixtures, players without a birth date.
//
// What this screen canNOT do (honest limit): impersonate a player or coach -
// per-role visibility probes need simulated JWT claims and run in maintenance
// sessions instead. This is the tripwire layer, not the full audit.

import { CONFIG } from './config.js';
import { DB } from './db.js';
import { t } from './strings.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const HEALTH = (() => {

  // Sensitive tables an anonymous visitor must never be able to read.
  const LOCKED_TABLES = [
    'evaluations', 'ratings', 'players', 'player_season_summary',
    'player_director_assessment', 'player_tracking_notes', 'team_anchors',
    'team_change_log', 'staff_evaluations', 'staff_feedback_notes',
    'baseline_results', 'dragons_score_snapshots', 'player_access_requests',
    'self_evals', 'self_eval_reviews', 'evaluation_translations',
  ];

  const CHIP = { ok: 'hc-chip hc-chip--ok', warn: 'hc-chip hc-chip--warn', fail: 'hc-chip hc-chip--fail' };

  let _rows = [];   // { name, status: 'ok'|'warn'|'fail'|'run', detail }

  async function show() {
    UI.showScreen('screen-health');
    _rows = [];
    _paint();
    await _run();
  }

  function _add(name, status, detail) {
    _rows.push({ name, status, detail });
    _paint();
  }

  function _paint() {
    const el = document.getElementById('health-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;
    el.innerHTML = `
      <div class="sel-detail-hint" style="margin-bottom:10px;">${esc(t('hc.hint'))}</div>
      ${_rows.map(r => `
        <div class="sel-code-row" style="align-items:flex-start;">
          <div style="flex:1; min-width:0;">
            <div class="sel-code-row__name">${esc(r.name)}</div>
            <div class="staff-row-counts">${esc(r.detail || '')}</div>
          </div>
          <span class="${CHIP[r.status] || CHIP.warn}">${esc(t('hc.' + r.status))}</span>
        </div>`).join('')}
      <div class="sel-detail-actions" style="margin-top:12px;">
        <button class="btn btn--secondary" id="hc-rerun">${esc(t('hc.rerun'))}</button>
      </div>`;
    document.getElementById('hc-rerun')?.addEventListener('click', show);
  }

  async function _run() {
    const season = CONFIG.season.current;

    // 1. Anonymous lockout - every sensitive table must refuse a stranger.
    try {
      const probes = await Promise.all(LOCKED_TABLES.map(DB.health.anonProbe));
      const open = probes.filter(p => !p.locked);
      _add(t('hc.anon'),
        open.length ? 'fail' : 'ok',
        open.length
          ? t('hc.anonFail', { tables: open.map(p => p.table).join(', ') })
          : t('hc.anonOk', { count: probes.length }));
    } catch { _add(t('hc.anon'), 'warn', t('hc.checkError')); }

    // 2. Reporting-view speed under the real director session (57014 lesson).
    try {
      const t0 = performance.now();
      await DB.playerSeasonSummary.list(season);
      const ms = Math.round(performance.now() - t0);
      _add(t('hc.viewSpeed'),
        ms < 2000 ? 'ok' : ms < 5000 ? 'warn' : 'fail',
        t('hc.viewSpeedDetail', { ms }));
    } catch { _add(t('hc.viewSpeed'), 'fail', t('hc.viewSpeedError')); }

    // 3. Blank-identity evaluations (the May 2026 director-eval bug).
    await _countCheck(t('hc.blankEvals'),
      `evaluations?team_code=eq.&author_staff_id=is.null&season_id=eq.${encodeURIComponent(season)}&select=id`,
      0, t('hc.blankEvalsBad'));

    // 4. Imported evaluations visible to players (the publish-leak bug).
    await _countCheck(t('hc.importedPublished'),
      'evaluations?or=(team_code.like.TG:*,team_code.like.TGIS:*)&published_to_player_at=not.is.null&select=id',
      0, t('hc.importedPublishedBad'));

    // 5. Snapshot freshness (trend history needs regular points).
    try {
      const last = await DB.ranking.lastSnapshotAt(season);
      if (!last) _add(t('hc.snapshots'), 'warn', t('hc.snapshotsNone'));
      else {
        const days = Math.floor((Date.now() - new Date(last).getTime()) / 86400000);
        _add(t('hc.snapshots'), days <= 8 ? 'ok' : 'warn', t('hc.snapshotsDetail', { days }));
      }
    } catch { _add(t('hc.snapshots'), 'warn', t('hc.checkError')); }

    // 6. Anchor coverage for the season's teams (informational).
    try {
      const [anchored, teams] = await Promise.all([
        DB.health.count(`team_anchors?season_id=eq.${encodeURIComponent(season)}&select=id`),
        DB.health.count(`memberships?season_id=eq.${encodeURIComponent(season)}&select=team_id`),
      ]);
      void teams;
      const distinctTeams = await DB.memberships.getAllForSeason(season)
        .then(ms => new Set((ms || []).map(m => m.team_id)).size).catch(() => null);
      _add(t('hc.anchors'), anchored > 0 ? 'ok' : 'warn',
        t('hc.anchorsDetail', { anchored: anchored ?? '?', teams: distinctTeams ?? '?' }));
    } catch { _add(t('hc.anchors'), 'warn', t('hc.checkError')); }

    // 7. Walkthrough fixtures still in the database?
    await _countCheck(t('hc.fixtures'), 'players?name=like.ZZ*&select=id',
      0, t('hc.fixturesBad'), 'warn');

    // 8. Players without a birth date (breaks birth-year cohorts for them).
    try {
      const n = await DB.health.count('players?date_of_birth=is.null&archived=eq.false&select=id');
      _add(t('hc.noDob'), n === 0 ? 'ok' : 'warn', t('hc.noDobDetail', { count: n }));
    } catch { _add(t('hc.noDob'), 'warn', t('hc.checkError')); }

    // Honest limit, always shown.
    _add(t('hc.roleProbes'), 'ok', t('hc.roleProbesDetail'));
  }

  async function _countCheck(name, path, expected, badText, badStatus = 'fail') {
    try {
      const n = await DB.health.count(path);
      _add(name, n === expected ? 'ok' : badStatus,
        n === expected ? t('hc.zeroOk') : `${badText} (${n})`);
    } catch { _add(name, 'warn', t('hc.checkError')); }
  }

  return { show };

})();
